import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { reactHookForm } from '@yola/ws-ui';
import { dialogs, siteLocales, integration, utils, i18next, site, view } from '@yola/ws-sdk';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import segment from 'src/js/modules/analytics/segment';
import isFeatureAvailable from 'src/js/modules/upsell/verifiers/is-feature-available';
import user from 'yola-editor/src/js/modules/user';
import AddLanguageDialog from '../components/add-language-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types';
import useCheckForUnsafe from '../hooks/use-check-for-unsafe';
import getLanguages from '../helpers/get-languages';
import getCountries from '../helpers/get-countries';
import getRelatedLanguageOptions from '../helpers/get-related-language-options';
import ai from '../../ai';
import reloadSiteMeta from '../../ai-wizard/helpers/reload-site-meta';
import updateLanguageSelector from '../helpers/update-language-selector';

const {
  hostingPackageFeatureNames: { AI_LOCALIZATION },
} = subscriptionManagerConstants;
const {
  constants: { errorTypes },
} = ai;

const { useForm } = reactHookForm;
const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

function AddLanguageDialogContainer({
  initialValues = null,
  onAfterLanguageAdded = utils.noop,
  onCancel = utils.noop,
  onDialogMainAction,
  onDialogCancel,
}) {
  const dispatch = useDispatch();

  const upsells = useSelector(integration.selectors.getUpsells);
  const pages = useSelector(site.selectors.getPages);
  const mainLocale = useSelector(siteLocales.selectors.getMainLocale);
  const mainLocaleHomePage = useSelector((state) => site.selectors.getHomePage(state, mainLocale));
  const availablePlatformComponents = useSelector(user.selectors.getAvailablePlatformComponents);

  const locales = useMemo(() => siteLocales.accessors.getSiteLocales(), []);
  const activePage = site.accessors.getActivePage();
  const langMap = useMemo(() => getLanguages(), []);
  const countriesMap = useMemo(() => getCountries(), []);

  const isAiLocalizationAvailable = isFeatureAvailable(AI_LOCALIZATION);

  const mainLocalePagesCount = useMemo(
    () => pages.filter((page) => page.locale === mainLocale).length,
    [pages, mainLocale]
  );

  const languageOptions = useMemo(
    () =>
      getRelatedLanguageOptions(locales, langMap, countriesMap).map((item) => ({
        value: item.code,
        iconComponent: item.iconComponent,
        label: item.name,
      })),
    [langMap, countriesMap, locales]
  );

  const computedInitialValues = useMemo(() => {
    const firstLangCode = languageOptions[0].value;
    return {
      locale: initialValues?.locale || firstLangCode,
      translateWithAi: initialValues?.translateWithAi || false,
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const { control, handleSubmit, getValues } = useForm({
    defaultValues: computedInitialValues,
    mode: 'onTouched',
  });

  const checkForUnsafe = useCheckForUnsafe();

  const forceReloadView = useCallback(() => {
    dispatch(view.actions.forceReloadView());
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const showProgressDialog = useCallback(
    (jobData) => {
      const succeeded = jobData?.results?.succeeded || {};
      const current = Object.values(succeeded).flat().length || 1;

      dialogs.operations.show(dialogTypes.TRANSLATION_PROGRESS_DIALOG, {
        overlay: 'visible',
        cancelable: false,
        progressType: 'indeterminate',
        width: 336,
        height: 109,
        minHeight: 109,
        captions: {
          title: `${i18next.t('Translating {current}/{total}...', {
            current,
            total: mainLocalePagesCount,
          })}`,
        },
      });
    },
    [mainLocalePagesCount]
  );

  const handleAddLocale = useCallback(
    async (values) => {
      const { locale, translateWithAi } = values;
      let results;

      showProgressDialog();

      try {
        const data = await siteLocales.helpers.initiateLanguageCreation({
          locale,
          translateWithAi,
          onPoll: showProgressDialog,
        });

        ({ results } = data);
      } catch (e) {
        if (translateWithAi) {
          const errorType = e.results?.code || errorTypes.UNKNOWN_TRANSLATE_PAGE;

          dialogs.operations.show(dialogTypes.AI_ERROR_DIALOG, {
            errorType,
            onRenew: () => {
              dialogs.operations.hide();
            },
          });
        } else {
          dialogs.operations.show(dialogTypes.ADD_LANGUAGE_ERROR_DIALOG, {
            captions: {
              title: i18next.t('Error during language addition'),
              description: i18next.t('An error occurred while adding the new language'),
            },
          });
        }

        return;
      }

      await reloadSiteMeta();
      trackEvent(events.WEBSITE_LANGUAGE_ADDED, { languageCode: locale });
      await updateLanguageSelector(forceReloadView);

      const failedPageIds = [
        ...new Set(Object.values(results.failed || {}).flatMap((pageMap) => Object.keys(pageMap))),
      ];

      if (failedPageIds.length > 0) {
        dialogs.operations.show(dialogTypes.TRANSLATION_ERROR_DIALOG, {
          failedPageIds,
          onRetry: async () => {
            if (mainLocale !== activePage.locale) {
              await site.operations.setActivePage(mainLocaleHomePage.id);
            }

            dialogs.operations.show(dialogTypes.TRANSLATE_PAGE, {
              initialValues: {
                pageIds: failedPageIds,
                locales: [locale],
                autoTrans: translateWithAi,
              },
              bulkEditing: false,
            });
          },
        });

        return;
      }

      onAfterLanguageAdded(values);
    },
    [
      showProgressDialog,
      onAfterLanguageAdded,
      mainLocale,
      activePage.locale,
      mainLocaleHomePage.id,
      forceReloadView,
    ]
  );

  const showSelf = useCallback(() => {
    dialogs.operations.show(dialogTypes.ADD_LANGUAGE, {
      onCancel,
      onAfterLanguageAdded,
      initialValues: getValues(),
    });
  }, [onCancel, onAfterLanguageAdded, getValues]);

  const guardedSubmit = useMemo(
    () =>
      checkForUnsafe({
        onSubmit: handleAddLocale,
        onCancel: showSelf,
      }),
    [checkForUnsafe, handleAddLocale, showSelf]
  );

  const handleDialogCancel = useCallback(() => {
    dialogs.operations.hide();
    onDialogCancel();
    onCancel();
  }, [onDialogCancel, onCancel]);

  const onFormSubmit = useMemo(
    () =>
      handleSubmit((values) => {
        onDialogMainAction({ targetLocale: values.locale, aiTranslation: values.translateWithAi });
        guardedSubmit(values);
      }),
    [handleSubmit, onDialogMainAction, guardedSubmit]
  );

  const onTranslateWithAiClick = useCallback(
    (e) => {
      if (!isAiLocalizationAvailable) {
        e.stopPropagation();
        dialogs.operations.hide();
        const { captions, premiumFeature, onUpgrade, purchaseFlowType } = upsells.aiLocalization;

        // b2c & reseller platform upgrade
        if (availablePlatformComponents.includes(user.platformComponents.PAYMENTS)) {
          onUpgrade().catch(() => {
            showSelf();
          });
          return;
        }

        dialogs.operations.show(dialogTypes.UPGRADE, {
          purchaseFlowType,
          premiumFeature,
          captions,
          onUpgrade,
          onUpgradeReject: showSelf,
        });
      }
    },
    [isAiLocalizationAvailable, upsells, showSelf, availablePlatformComponents]
  );

  return (
    <AddLanguageDialog
      control={control}
      languageOptions={languageOptions}
      onFormSubmit={onFormSubmit}
      onCancel={handleDialogCancel}
      isAiLocalizationAvailable={isAiLocalizationAvailable}
      onTranslateWithAiClick={onTranslateWithAiClick}
    />
  );
}

AddLanguageDialogContainer.propTypes = {
  initialValues: PropTypes.shape(),
  onCancel: PropTypes.func,
  onAfterLanguageAdded: PropTypes.func,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default AddLanguageDialogContainer;
